import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsAPI } from '../utils/api';
import ProductCard from '../components/ProductCard';
import SEO, { SITE_URL } from '../components/SEO';
import { toast } from 'sonner';
import { Filter } from 'lucide-react';

const CATEGORY_SLUG_MAP = {
  'saree': 'Saree',
  'lehenga': 'Lehenga',
  'pakistani-suit': 'Pakistani Suit',
  'indo-western': 'Indo-Western',
  'family-outfit': 'Family Outfit',
};

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const rawCategory = searchParams.get('category') || '';
  const resolvedCategory = CATEGORY_SLUG_MAP[rawCategory] || rawCategory;
  const [filters, setFilters] = useState({
    category: resolvedCategory,
    sort_by: '',
  });

  useEffect(() => {
    const raw = searchParams.get('category') || '';
    const resolved = CATEGORY_SLUG_MAP[raw] || raw;
    setFilters(prev => ({ ...prev, category: resolved }));
  }, [searchParams]);

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.category) params.category = filters.category;
        if (filters.sort_by) params.sort_by = filters.sort_by;
        const { data } = await productsAPI.getAll(params);
        setProducts(data);
      } catch (_) {
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'category') {
      if (value) {
        searchParams.set('category', value);
      } else {
        searchParams.delete('category');
      }
      setSearchParams(searchParams);
    }
  };

  const categories = ['Saree', 'Lehenga', 'Pakistani Suit', 'Indo-Western', 'Family Outfit'];

  const categoryTitle = filters.category ? `${filters.category} Collection` : 'Shop All Ethnic Wear';
  const categoryDesc = filters.category
    ? `Browse our ${filters.category} collection. Premium handcrafted ethnic wear by Studio Crochet. COD available.`
    : 'Shop premium ethnic wear. Sarees, Lehengas, Pakistani Suits, Indo-Western outfits. COD available. Free shipping.';

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="shop-page">
      <SEO
        title={categoryTitle}
        description={categoryDesc}
        canonical={filters.category ? `${SITE_URL}/shop?category=${rawCategory || filters.category.toLowerCase().replace(/\s+/g, '-')}` : `${SITE_URL}/shop`}
      />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl leading-none mb-8" data-testid="shop-heading">Shop All</h1>
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-12" data-testid="filters-section">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            <span className="font-outfit text-sm uppercase tracking-wider">Filters:</span>
          </div>
          
          {/* Category Filter */}
          <select 
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="border-b border-[#EBE4D5] bg-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-[#C29B57] font-outfit text-base outline-none"
            data-testid="category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Sort Filter */}
          <select 
            value={filters.sort_by}
            onChange={(e) => handleFilterChange('sort_by', e.target.value)}
            className="border-b border-[#EBE4D5] bg-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-[#C29B57] font-outfit text-base outline-none"
            data-testid="sort-filter"
          >
            <option value="">Sort By</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20" data-testid="loading-indicator">
            <p className="font-outfit text-[#8C8577]">Loading...</p>
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-12" data-testid="products-grid">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-products">
            <p className="font-outfit text-[#8C8577]">No products found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;